import type { ChatMessage } from "@cc/protocol";

/**
 * Provider abstraction.
 *
 * Keeping the model behind this interface is what makes the whole project
 * testable without spending a cent: every test and every ladder run can swap in
 * a deterministic fake that returns canned or scripted actions.
 */
export type { ChatMessage };

export interface ModelProvider {
  readonly name: string;
  complete(messages: readonly ChatMessage[]): Promise<string>;
}

export class ProviderError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = "ProviderError";
  }
}

/* ------------------------------------------------------------------ */
/* OpenAI-compatible provider (works for OpenAI, DeepSeek, Qwen, etc.)  */
/* ------------------------------------------------------------------ */

export interface OpenAiCompatibleOptions {
  readonly name: string;
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly model: string;
  readonly temperature?: number;
  readonly maxTokens?: number;
  readonly timeoutMs?: number;
  /**
   * Ask the provider to constrain the reply to a JSON object.
   *
   * Default on: the arena only accepts a single JSON action. DeepSeek documents
   * that JSON mode can occasionally return empty `content`; we treat that as a
   * retryable miss rather than a hard failure.
   */
  readonly jsonMode?: boolean;
  /**
   * Send `thinking: { type: "disabled" }` for structured action turns.
   *
   * DeepSeek V4 defaults thinking to *enabled*, and their docs warn against
   * combining thinking with `response_format: json_object`. Thinking also shares
   * the `max_tokens` budget with the final answer — so a 256-token cap quietly
   * returns HTTP 200 with empty `content`, which we used to surface as
   * `[fallback] provider error`. Default on for game decisions.
   */
  readonly disableThinking?: boolean;
  /** Extra attempts after the first try for empty content / 429 / 5xx. */
  readonly maxRetries?: number;
  /** Injected for tests. */
  readonly fetchImpl?: typeof fetch;
}

/**
 * Defaults tuned for *action JSON*, not chat.
 *
 * Temperature used to default to 0.8, which is fine for creative writing and
 * terrible for a six-verb schema. JSON mode plus a cooler sample keeps the
 * entertainment in `reason`, not in the shape of the reply.
 *
 * `max_tokens` must leave room for a full JSON object even if a gateway ignores
 * `thinking: disabled` and still burns some reasoning tokens.
 */
const DEFAULT_TEMPERATURE = 0.3;
const DEFAULT_MAX_TOKENS = 384;
const DEFAULT_RETRIES = 2;

export function createOpenAiCompatibleProvider(opts: OpenAiCompatibleOptions): ModelProvider {
  const doFetch = opts.fetchImpl ?? fetch;
  const timeoutMs = opts.timeoutMs ?? 20_000;
  const jsonModePreferred = opts.jsonMode !== false;
  const thinkingOffPreferred = opts.disableThinking !== false;
  const maxRetries = opts.maxRetries ?? DEFAULT_RETRIES;

  return {
    name: opts.name,
    async complete(messages) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        let useJson = jsonModePreferred;
        let disableThinking = thinkingOffPreferred;
        let lastError: ProviderError | null = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          if (attempt > 0) {
            await sleep(backoffMs(attempt), controller.signal);
          }

          const body: Record<string, unknown> = {
            model: opts.model,
            temperature: opts.temperature ?? DEFAULT_TEMPERATURE,
            max_tokens: opts.maxTokens ?? DEFAULT_MAX_TOKENS,
            messages,
          };
          if (useJson) body.response_format = { type: "json_object" };
          // DeepSeek-shaped field. Unknown to other gateways; they usually ignore
          // it. If one rejects it we strip on the next attempt (see below).
          if (disableThinking) body.thinking = { type: "disabled" };

          let res: Response;
          try {
            res = await doFetch(`${opts.baseUrl.replace(/\/$/, "")}/chat/completions`, {
              method: "POST",
              headers: {
                "content-type": "application/json",
                authorization: `Bearer ${opts.apiKey}`,
              },
              body: JSON.stringify(body),
              signal: controller.signal,
            });
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
              throw new ProviderError(`${opts.name} timed out after ${timeoutMs}ms`, true);
            }
            lastError = new ProviderError(
              `${opts.name} request failed: ${String(err)}`,
              true,
            );
            continue;
          }

          if (!res.ok) {
            const errBody = await safeText(res);
            // Strip unsupported constraint knobs rather than failing the turn.
            if (res.status === 400 && useJson && /response_format|json_object/i.test(errBody)) {
              useJson = false;
              attempt--; // don't burn a retry budget on a shape fix
              continue;
            }
            if (res.status === 400 && disableThinking && /thinking/i.test(errBody)) {
              disableThinking = false;
              attempt--;
              continue;
            }
            const retryable = res.status >= 500 || res.status === 429;
            lastError = new ProviderError(
              `${opts.name} HTTP ${res.status}: ${errBody.slice(0, 200)}`,
              retryable,
            );
            if (retryable && attempt < maxRetries) continue;
            throw lastError;
          }

          const json = (await res.json()) as {
            choices?: {
              finish_reason?: string;
              message?: { content?: unknown; reasoning_content?: unknown };
            }[];
          };
          const choice = json.choices?.[0];
          const content = extractMessageText(choice?.message);
          if (content !== null) return content;

          // DeepSeek JSON mode is documented to occasionally return empty
          // content with HTTP 200. Retry the identical request; do not treat it
          // as an unparseable model reply.
          lastError = new ProviderError(
            `${opts.name} returned no content` +
              (choice?.finish_reason ? ` (finish_reason=${choice.finish_reason})` : ""),
            true,
          );
          if (attempt < maxRetries) continue;
          throw lastError;
        }

        throw lastError ?? new ProviderError(`${opts.name} failed after retries`, true);
      } catch (err) {
        if (err instanceof ProviderError) throw err;
        if (err instanceof Error && err.name === "AbortError") {
          throw new ProviderError(`${opts.name} timed out after ${timeoutMs}ms`, true);
        }
        throw new ProviderError(`${opts.name} request failed: ${String(err)}`, true);
      } finally {
        clearTimeout(timer);
      }
    },
  };
}

function backoffMs(attempt: number): number {
  // ~250ms, 500ms, 1000ms — short enough that the match still feels live.
  return Math.min(1000, 250 * 2 ** (attempt - 1));
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      const err = new Error("aborted");
      err.name = "AbortError";
      reject(err);
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      const err = new Error("aborted");
      err.name = "AbortError";
      reject(err);
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Pulls the assistant text out of an OpenAI-shaped message.
 *
 * Content may be a string or an array of typed parts. We never treat
 * `reasoning_content` as the action JSON — thinking is disabled for game turns
 * precisely so the budget is spent on the final object.
 */
function extractMessageText(
  message: { content?: unknown; reasoning_content?: unknown } | undefined,
): string | null {
  if (!message) return null;
  return coalesceText(message.content);
}

function coalesceText(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? value : null;
  }
  if (Array.isArray(value)) {
    const parts: string[] = [];
    for (const part of value) {
      if (typeof part === "string") parts.push(part);
      else if (part && typeof part === "object") {
        const rec = part as Record<string, unknown>;
        if (typeof rec.text === "string") parts.push(rec.text);
        else if (typeof rec.content === "string") parts.push(rec.content);
      }
    }
    const joined = parts.join("");
    return joined.trim().length > 0 ? joined : null;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Anthropic provider                                                  */
/* ------------------------------------------------------------------ */

export interface AnthropicOptions {
  readonly name?: string;
  readonly apiKey: string;
  readonly model: string;
  readonly baseUrl?: string;
  readonly maxTokens?: number;
  readonly timeoutMs?: number;
  readonly temperature?: number;
  /** Extra attempts after the first try for empty content / 429 / 5xx. */
  readonly maxRetries?: number;
  readonly fetchImpl?: typeof fetch;
}

export function createAnthropicProvider(opts: AnthropicOptions): ModelProvider {
  const doFetch = opts.fetchImpl ?? fetch;
  const name = opts.name ?? "anthropic";
  const timeoutMs = opts.timeoutMs ?? 20_000;
  const maxRetries = opts.maxRetries ?? DEFAULT_RETRIES;
  return {
    name,
    async complete(messages) {
      const system = messages.filter((m) => m.role === "system").map((m) => m.content).join("\n\n");
      const rest = messages
        .filter((m) => m.role !== "system")
        .map((m) => ({ role: m.role, content: m.content }));
      if (rest.length === 0) rest.push({ role: "user" as const, content: "Decide." });

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        let lastError: ProviderError | null = null;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          if (attempt > 0) await sleep(backoffMs(attempt), controller.signal);

          let res: Response;
          try {
            res = await doFetch(
              `${(opts.baseUrl ?? "https://api.anthropic.com").replace(/\/$/, "")}/v1/messages`,
              {
                method: "POST",
                headers: {
                  "content-type": "application/json",
                  "x-api-key": opts.apiKey,
                  "anthropic-version": "2023-06-01",
                },
                body: JSON.stringify({
                  model: opts.model,
                  max_tokens: opts.maxTokens ?? DEFAULT_MAX_TOKENS,
                  system,
                  messages: rest,
                  temperature: opts.temperature ?? DEFAULT_TEMPERATURE,
                }),
                signal: controller.signal,
              },
            );
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
              throw new ProviderError(`${name} timed out after ${timeoutMs}ms`, true);
            }
            lastError = new ProviderError(`${name} request failed: ${String(err)}`, true);
            continue;
          }

          if (!res.ok) {
            const errBody = await safeText(res);
            const retryable = res.status >= 500 || res.status === 429;
            lastError = new ProviderError(
              `${name} HTTP ${res.status}: ${errBody.slice(0, 200)}`,
              retryable,
            );
            if (retryable && attempt < maxRetries) continue;
            throw lastError;
          }

          const json = (await res.json()) as { content?: { type: string; text?: string }[] };
          const text = json.content?.find((c) => c.type === "text")?.text;
          if (typeof text === "string" && text.trim().length > 0) return text;

          lastError = new ProviderError(`${name} returned no text content`, true);
          if (attempt < maxRetries) continue;
          throw lastError;
        }
        throw lastError ?? new ProviderError(`${name} failed after retries`, true);
      } catch (err) {
        if (err instanceof ProviderError) throw err;
        if (err instanceof Error && err.name === "AbortError") {
          throw new ProviderError(`${name} timed out after ${timeoutMs}ms`, true);
        }
        throw new ProviderError(`${name} request failed: ${String(err)}`, true);
      } finally {
        clearTimeout(timer);
      }
    },
  };
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return "<unreadable body>";
  }
}

/* ------------------------------------------------------------------ */
/* Scripted provider, for tests and replays                            */
/* ------------------------------------------------------------------ */

export function createScriptedProvider(name: string, script: readonly string[]): ModelProvider {
  let i = 0;
  return {
    name,
    async complete() {
      const value = script[Math.min(i, script.length - 1)] ?? '{"action":"WAIT"}';
      i++;
      return value;
    },
  };
}

export function createConstantProvider(name: string, response: string): ModelProvider {
  return {
    name,
    async complete() {
      return response;
    },
  };
}
